import React, { ReactNode, useEffect } from 'react';
import { DictionaryService } from 'service/dictionary/dictionaryService';
import parseRecipeStep from './helpers/parseRecipeStep';
import type TRecipeStep from '../../types/RecipeStep';
import type { IsettingRateData } from 'types/SettingRateData';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import { Title } from '../Typography';
import { RatingBanner } from '../RatingBanner';
import { Ingredient, Separator, Item, SubStep } from './styles';
import NextImage from 'components/NextImage';
import { Video } from 'components/RichTextRenderer/templates/Video';
import { usePathname } from 'next/navigation';
import { NumberItemComponent } from 'components/RecipeStep/components/NumberItemComponent';

type RecipeStepImage = {
  Title: string;
  Formats: {
    Height: number;
    Width: number;
    MimeType: string;
    Url: string;
  }[];
};

export const RecipeStep = ({
  steps,
  showAssets,
  dictionary,
  isFaqStructure = false,
  showRatingBanner = false,
  ratingMsg,
  ratingData,
  settingRateData,
  trackingEvent,
  trackStep,
  recipeTitle,
  showCommentField = false,
  showContactPageData = false,
  contactPageData = '',
}: {
  steps: any;
  showAssets: boolean;
  dictionary: DictionaryItem[];
  isFaqStructure?: boolean;
  showRatingBanner?: boolean;
  ratingMsg?: any;
  ratingData?: any;
  settingRateData?: IsettingRateData;
  trackingEvent?: (value: number, title: string) => void;
  trackStep?: (recipeTitle: string, step: number) => void;
  recipeTitle: string;
  showCommentField?: boolean;
  showContactPageData?: boolean;
  contactPageData?: string;
}): ReactNode => {
  const pathname = usePathname();
  let itemsToBeRemoved: number[] = [];
  let stepNumber: number = 0;
  const data = steps.map((step: any, index: number) => {
    // Parse recipe step into concise object
    const recipeStep: TRecipeStep = parseRecipeStep(step);
    const innerSteps: TRecipeStep[] = [];
    if (!itemsToBeRemoved.includes(index)) {
      for (let i = index + 1; i < steps.length; i += 1) {
        const tempRecipeStep: TRecipeStep = parseRecipeStep(steps[i]);
        if (!tempRecipeStep.isStep) {
          innerSteps.push(tempRecipeStep);
          itemsToBeRemoved.push(i);
        } else {
          break;
        }
      }
    }

    const title = recipeStep.title || '';
    const isStep = recipeStep?.isStep || false;

    const generateAssets = (item: TRecipeStep) => (
      <>
        {item.images !== undefined && item.images.length !== 0 && (
          <div className="w-full md:pr-12">
            {item.images.map((image: RecipeStepImage) => (
              <div className="mb-[1.125rem] relative md:mb-7" key={image.Formats[0].Url}>
                <NextImage
                  src={image.Formats[0].Url}
                  alt={image.Title}
                  className="object-cover"
                  width={image?.Formats[0].Width}
                  height={image?.Formats[0].Height}
                  mimeType={image?.Formats[0].MimeType}
                />
              </div>
            ))}
          </div>
        )}
        {!!item.videos?.length && (
          <div className="w-full md:pr-12">
            <Video
              src={`https://www.youtube.com/embed/${item.videos[0].YouTubeVideoLink.split('=')[1]}`}
              video={item.videos[0].YouTubeVideoLink}
              width={item.videos[0].width}
              height={item.videos[0].height}
              title={item.videos[0].title}
              name={item.videos[0].name}
              description={item.videos[0].description}
              thumbnailUrls={item.videos[0].thumbnailUrl}
              embedUrl={item.videos[0].embedUrl}
              uploadDate={item.videos[0].uploadDate}
              duration={item.videos[0].duration}
              interactionCount={item.videos[0].interactionStatistic?.userInteractionCount}
            />
          </div>
        )}
      </>
    );

    const generateSubStep = (innerRecipeStep: TRecipeStep) => (
      <>
        {innerRecipeStep.description ? (
          <p
            className="text-base mb-5 step-links"
            dangerouslySetInnerHTML={{ __html: innerRecipeStep.description }}
          />
        ) : (
          ''
        )}
        {innerRecipeStep.rackPosition ? (
          <SubStep>
            {DictionaryService.getCopy('rack_position', dictionary)}: {innerRecipeStep.rackPosition}
          </SubStep>
        ) : (
          ''
        )}
        {innerRecipeStep.cookingSummaryText ? (
          <SubStep>{innerRecipeStep.cookingSummaryText}</SubStep>
        ) : (
          ''
        )}
        {innerRecipeStep.conventionalOvenTemp ? (
          <SubStep>
            {DictionaryService.getCopy('top_and_bottom_heat_oven_temperature', dictionary)}:{' '}
            {innerRecipeStep.conventionalOvenTemp}
          </SubStep>
        ) : (
          ''
        )}
        {innerRecipeStep.fanOvenTemp ? (
          <SubStep>
            {DictionaryService.getCopy('fan_assisted_oven_temperature', dictionary)}:{' '}
            {innerRecipeStep.fanOvenTemp}
          </SubStep>
        ) : (
          ''
        )}
      </>
    );

    const body = (
      <div
        className="mb-5 sm:mr-10 mt-2"
        {...(isFaqStructure && {
          itemScope: true,
          itemProp: 'acceptedAnswer',
          itemType: 'https://schema.org/Answer',
        })}
      >
        {showAssets && generateAssets(recipeStep)}
        <div {...(isFaqStructure && { itemProp: 'text' })}>
          {recipeStep?.ingredients?.length ? (
            <>
              <div className="w-full md:mb-5 mt-0.5">
                {recipeStep?.ingredients &&
                  recipeStep?.ingredients.map((ingredient: any, idx: number) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Ingredient key={`ingredient-${idx}`}>
                      <span className="text-lg pr-1.5 leading-6 font-primary">
                        {ingredient.AmountAndUnitSummaryText}
                      </span>
                      <span className="md:text-base sm:text-sm  sm:leading-6 md:leading-relaxed font-primary">
                        {ingredient.ArticleSummaryText}
                      </span>
                    </Ingredient>
                  ))}
              </div>
              <Separator />
            </>
          ) : (
            ''
          )}
          <div className="w-full">
            {generateSubStep(recipeStep)}

            {innerSteps.length > 0 &&
              innerSteps.map((item: TRecipeStep, idx: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={`recipe-step-${idx}`}>
                  <br />
                  {item.title && (
                    <Title tag="div" type="sm" className="mb-8">
                      {item.title}
                    </Title>
                  )}
                  {showAssets && generateAssets(item)}
                  {generateSubStep(item)}
                </React.Fragment>
              ))}
          </div>
        </div>
      </div>
    );
    return {
      title,
      body,
      isStep,
    };
  });

  itemsToBeRemoved = itemsToBeRemoved.sort((a, b) => b - a);

  itemsToBeRemoved.forEach((item) => {
    data.splice(item, 1);
  });

  const refArray = React.useRef([] as any[]);

  // Observe preparation-blocks-steps
  useEffect(() => {
    refArray.current
      ?.filter((item) => item)
      .map((numberRefItem: any, index: number) => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && trackStep) {
                trackStep(recipeTitle, index);
              }
            });
          },
          {
            rootMargin: '0px',
            threshold: 0.5,
          },
        );

        const currElement = numberRefItem!;
        observer.observe(currElement);
        return () => observer.unobserve(currElement);
      });
  }, [pathname]);

  const PanelContent = (item: any, index: number) => {
    if (item?.isStep) {
      stepNumber++;
    }
    return (
      <Item
        className="preparation-blocks-steps break-inside-auto"
        key={`${index}-recipe-step`}
        ref={(numberRef: HTMLDivElement) => {
          refArray.current[index] = numberRef;
        }}
      >
        {item?.isStep && (
          <NumberItemComponent
            step={stepNumber}
            dataTest={`${index}-number-container`}
            dataIndex={stepNumber}
          />
        )}
        <div
          {...(isFaqStructure && {
            itemScope: true,
            itemProp: 'mainEntity',
            itemType: 'https://schema.org/Question',
          })}
        >
          {!!item.title && (
            <Title
              tag={'div'}
              type={'sm'}
              className={'mt-2 mb-4 md:mt-0'}
              {...(isFaqStructure && { itemProp: 'name' })}
            >
              {item.title}
            </Title>
          )}
          {item.body}
        </div>
      </Item>
    );
  };

  const combinedItems = [...data];
  if (showRatingBanner && settingRateData) {
    combinedItems.push({
      isStep: true,
      title: ratingData.rate_this || 'Rate the recipe',
      body: (
        <>
          <>{ratingData.rating_banner_text}</>
          <div className="mb-5 md:mr-10 mt-2">
            <RatingBanner
              ratingData={ratingData}
              ratingMsg={ratingMsg}
              settingRateData={settingRateData}
              recipeTitle={recipeTitle}
              showCommentField={showCommentField}
              showContactPageData={showContactPageData}
              contactPageData={contactPageData}
              trackingEvent={trackingEvent}
              isPreparationStep
            />
          </div>
        </>
      ),
    });
  }

  return <div data-test="recipe-step-accordion">{combinedItems.map(PanelContent)}</div>;
};
